// Inline gallery for `images` artifacts emitted by the image_director
// subagent's generate_images tool. Renders a 2x2 grid of generated images;
// click any thumbnail to open a fullscreen lightbox.
//
// The composed prompt is collapsed by default — the chat is the UI, not
// the prompt log. Click "Show prompt" to expand it for inspection /
// debugging / copying. "Pin to moodboard" is a placeholder for the v1.5
// moodboard-routing flow; for now the user manages images per-thread.

import { ChevronDown, ChevronUp, Copy, Pin, X } from "lucide-react";
import { useEffect, useState } from "react";

import { cn } from "@/lib/utils";

interface GalleryImage {
  url: string;
  generation_id: string;
  aspect_ratio?: string;
}

interface GalleryArtifact {
  id?: string;
  prompt?: string;
  aspect_ratio?: string;
  ref_ids?: string[];
  images?: GalleryImage[];
}

export function ImageGalleryCard({ data }: { data: GalleryArtifact }) {
  const images = Array.isArray(data?.images) ? data.images : [];
  const [showPrompt, setShowPrompt] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);
  const [copied, setCopied] = useState(false);

  // Empty / failure state — generate_images returned but every variant
  // failed. Better to say so than render a confused empty card.
  if (images.length === 0) {
    return (
      <div className="rounded-xl border border-destructive/30 bg-destructive/5 p-5 text-sm text-destructive">
        Image generation finished with no images. The agent's reply has the
        details — try asking again or adjusting the prompt.
      </div>
    );
  }

  const aspect = data.aspect_ratio || images[0]?.aspect_ratio || "1:1";
  // Tailwind doesn't ship aspect classes for arbitrary ratios, so we
  // inline the aspect-ratio CSS prop.
  const aspectStyle = aspect.includes(":")
    ? { aspectRatio: aspect.replace(":", " / ") }
    : { aspectRatio: "1 / 1" };

  // Image count drives column count — 1 = single, 2 = side-by-side, 3-4 = 2x2.
  const cols =
    images.length === 1 ? "grid-cols-1" : "grid-cols-2";

  const copyPrompt = async () => {
    if (!data.prompt) return;
    try {
      await navigator.clipboard.writeText(data.prompt);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch (e) {
      console.error("[gallery] copy failed", e);
    }
  };

  return (
    <>
      <div className="rounded-xl border border-border bg-card shadow-card overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between gap-3 border-b border-border px-4 py-2.5">
          <p className="text-tiny tracking-[0.18em] uppercase text-muted-foreground">
            Generated · {images.length} image
            {images.length !== 1 ? "s" : ""} · {aspect}
          </p>
        </div>

        {/* Grid */}
        <div className={cn("grid gap-2 p-2", cols)}>
          {images.map((img, i) => (
            <button
              key={img.generation_id}
              type="button"
              onClick={() => setLightboxIndex(i)}
              className={cn(
                "group relative overflow-hidden rounded-md border border-border bg-muted",
                "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/40"
              )}
              style={aspectStyle}
              aria-label={`Open image ${i + 1} fullscreen`}
            >
              <img
                src={img.url}
                alt={`Generated image ${i + 1}`}
                className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-[1.02]"
                loading="lazy"
              />
            </button>
          ))}
        </div>

        {/* Footer — prompt toggle + (placeholder) pin */}
        {data.prompt && (
          <div className="border-t border-border">
            <button
              type="button"
              onClick={() => setShowPrompt((v) => !v)}
              className="flex w-full items-center justify-between px-4 py-2.5 text-xs text-muted-foreground hover:bg-accent/40 transition-colors"
            >
              <span className="flex items-center gap-1.5">
                {showPrompt ? (
                  <ChevronUp className="h-3.5 w-3.5" />
                ) : (
                  <ChevronDown className="h-3.5 w-3.5" />
                )}
                {showPrompt ? "Hide prompt" : "Show prompt"}
              </span>
            </button>
            {showPrompt && (
              <div className="border-t border-border bg-muted/30 px-4 py-3 space-y-2">
                <pre className="whitespace-pre-wrap text-[12px] leading-relaxed text-muted-foreground font-mono max-h-64 overflow-auto">
                  {data.prompt}
                </pre>
                <div className="flex items-center gap-2 pt-1">
                  <button
                    type="button"
                    onClick={copyPrompt}
                    className={cn(
                      "inline-flex items-center gap-1.5 rounded-md border border-border px-2.5 py-1 text-xs transition-colors",
                      copied
                        ? "bg-foreground text-background border-foreground"
                        : "hover:bg-accent"
                    )}
                  >
                    <Copy className="h-3 w-3" />
                    {copied ? "Copied" : "Copy prompt"}
                  </button>
                  <button
                    type="button"
                    title="Pin to moodboard (coming soon)"
                    disabled
                    className="inline-flex items-center gap-1.5 rounded-md border border-border px-2.5 py-1 text-xs text-muted-foreground/50 cursor-not-allowed"
                  >
                    <Pin className="h-3 w-3" />
                    Pin to moodboard
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Lightbox */}
      {lightboxIndex !== null && (
        <Lightbox
          images={images}
          index={lightboxIndex}
          onClose={() => setLightboxIndex(null)}
          onPrev={() =>
            setLightboxIndex((i) =>
              i === null ? null : (i - 1 + images.length) % images.length
            )
          }
          onNext={() =>
            setLightboxIndex((i) =>
              i === null ? null : (i + 1) % images.length
            )
          }
        />
      )}
    </>
  );
}

function Lightbox({
  images,
  index,
  onClose,
  onPrev,
  onNext,
}: {
  images: GalleryImage[];
  index: number;
  onClose: () => void;
  onPrev: () => void;
  onNext: () => void;
}) {
  const current = images[index];

  // Esc to close, arrow keys to navigate.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
      else if (e.key === "ArrowLeft") onPrev();
      else if (e.key === "ArrowRight") onNext();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose, onPrev, onNext]);

  if (!current) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-6"
      onClick={onClose}
    >
      <button
        type="button"
        onClick={onClose}
        className="absolute right-4 top-4 rounded-full bg-white/10 p-2 text-white hover:bg-white/20 transition-colors"
        aria-label="Close"
      >
        <X className="h-5 w-5" />
      </button>
      <img
        src={current.url}
        alt={`Generated image ${index + 1}`}
        className="max-h-[90vh] max-w-[90vw] rounded-md shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      />
      {images.length > 1 && (
        <p className="absolute bottom-4 left-1/2 -translate-x-1/2 rounded-full bg-white/10 px-3 py-1 text-xs text-white tracking-wider">
          {index + 1} / {images.length}
        </p>
      )}
    </div>
  );
}
